class ProductCollectionNav {
  constructor(container) {
    this.container = container;
    this.productHandle = container.dataset.productHandle;
    this.collectionHandle = container.dataset.collectionHandle || '';
    this.collectionTitle = container.dataset.collectionTitle || '';
    this.vendorHandle = container.dataset.vendorHandle || '';
    this.vendorCollectionTitle = container.dataset.vendorCollectionTitle || '';

    // Parse the product's collection membership for referrer validation
    const rawHandles = container.dataset.productCollectionHandles || '';
    const rawTitles = container.dataset.productCollectionTitles || '';
    const handles = rawHandles ? rawHandles.split(',') : [];
    const titles = rawTitles ? rawTitles.split('|||') : [];
    this.productCollections = handles.map((h, i) => ({ handle: h, title: titles[i] || '' }));

    this.prevBtn = container.querySelector('.product-collection-nav__btn--prev');
    this.nextBtn = container.querySelector('.product-collection-nav__btn--next');

    this.init();
  }

  // Returns the collection handle extracted from the referring page URL, if the
  // referrer is the same origin and contains a /collections/{handle} path segment.
  getReferrerCollectionHandle() {
    try {
      if (!document.referrer) return null;
      const ref = new URL(document.referrer);
      if (ref.origin !== window.location.origin) return null;
      const match = ref.pathname.match(/^\/collections\/([^/]+)/);
      return match ? match[1] : null;
    } catch (e) {
      return null;
    }
  }

  async init() {
    try {
      let handle = '';
      let label = '';
      let isCollection = false;

      if (this.collectionHandle) {
        // 1. Collection is in the current URL (highest priority)
        handle = this.collectionHandle;
        label = this.collectionTitle;
        isCollection = true;
      } else {
        // 2. Check referrer — did the customer click through from a collection page?
        const referrerHandle = this.getReferrerCollectionHandle();
        if (referrerHandle) {
          const match = this.productCollections.find((c) => c.handle === referrerHandle);
          if (match) {
            handle = match.handle;
            label = match.title;
            isCollection = true;
          }
        }

        // 3. Vendor collection fallback
        if (!handle && this.vendorHandle) {
          handle = this.vendorHandle;
          label = this.vendorCollectionTitle;
          isCollection = false;
        }
      }

      if (!handle) {
        this.container.classList.add('product-collection-nav--empty');
        return;
      }

      const products = await this.fetchAllProducts(handle);
      if (!products || products.length <= 1) {
        this.container.classList.add('product-collection-nav--empty');
        return;
      }

      this.navigate(products, handle, label, isCollection);
      this.container.classList.add('product-collection-nav--ready');
    } catch (e) {
      // Fail silently — collapse the reserved space
      this.container.classList.add('product-collection-nav--empty');
    }
  }

  async fetchAllProducts(handle) {
    const cacheKey = `pcn-${handle}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch (e) {
        sessionStorage.removeItem(cacheKey);
      }
    }

    const all = [];
    let page = 1;
    const limit = 250;

    while (true) {
      const url = `/collections/${handle}/products.json?limit=${limit}&page=${page}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      const products = data.products || [];
      all.push(...products);

      if (products.length < limit) break;
      page++;
    }

    if (all.length > 0) {
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify(all));
      } catch (e) {
        // sessionStorage quota exceeded — continue without caching
      }
    }

    return all;
  }

  navigate(products, collectionHandle, labelText, isCollection) {
    const index = products.findIndex((p) => p.handle === this.productHandle);
    if (index === -1) return;

    const total = products.length;
    const prevIndex = (index - 1 + total) % total;
    const nextIndex = (index + 1) % total;

    const prevProduct = products[prevIndex];
    const nextProduct = products[nextIndex];

    this.updateButton(
      this.prevBtn,
      prevProduct,
      collectionHandle,
      'previous product'
    );

    this.updateButton(
      this.nextBtn,
      nextProduct,
      collectionHandle,
      'next product'
    );
  }

  updateButton(btn, product, collectionHandle, ariaContext) {
    btn.href = `/collections/${collectionHandle}/products/${product.handle}`;
    btn.setAttribute('aria-label', `${ariaContext}: ${product.title}`);
  }
}

document.querySelectorAll('.product-collection-nav').forEach((el) => {
  new ProductCollectionNav(el);
});
